/**
 * ApiRequestTracker — Proxy wrapper for Playwright's APIRequestContext
 *
 * Intercepts all HTTP method calls (.get, .post, .put, .patch, .delete,
 * .head, .fetch, .dispose) to record detailed API call analytics.
 * Data is flushed to testInfo.annotations for the reporter to consume.
 */

import { performance } from 'node:perf_hooks';
import type { APIRequestContext, APIResponse } from '@playwright/test';
import type { ApiCallRecord, ApiCallsByStatusRange, CapturedCookie } from '@testrelic/core';
import { JsonlFileWriter } from './jsonl-stream.js';
import { CALL_ID_SYMBOL, valueCallIdMap } from './assertion-tracker.js';
import type { AssertionTracker } from './assertion-tracker.js';
import type { ResolvedApiConfig } from './config.js';
import { redactHeaders, redactBodyFields, stripSecretQueryParams } from './api-redactor.js';
import { shouldTrackApiUrl } from './api-url-filter.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const HTTP_METHODS = ['get', 'post', 'put', 'patch', 'delete', 'head', 'fetch'] as const;
type HttpMethod = (typeof HTTP_METHODS)[number];

const TEXT_CONTENT_TYPES = [
  'text/',
  'application/json',
  'application/xml',
  'application/javascript',
  'application/x-www-form-urlencoded',
  'application/graphql',
];

const ANNOTATION_TYPE = '__testrelic_api_calls';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isTextContentType(contentType: string): boolean {
  const lower = contentType.toLowerCase();
  return TEXT_CONTENT_TYPES.some((prefix) => lower.includes(prefix));
}

/**
 * Extract request body from Playwright request options.
 * Handles `data` (string/object/Buffer), `form` (key-value), and
 * `multipart` (field descriptors) cases.
 */
function extractRequestBody(options?: Record<string, unknown> | null): string | null {
  if (!options) return null;

  if (options.data !== undefined && options.data !== null) {
    const data = options.data;
    if (typeof data === 'string') return data;
    if (Buffer.isBuffer(data)) return data.toString('base64');
    return JSON.stringify(data);
  }

  if (options.form !== undefined && options.form !== null) {
    return JSON.stringify(options.form);
  }

  if (options.multipart !== undefined && options.multipart !== null) {
    // Capture field names and metadata, not full binary contents
    const multipart = options.multipart as Record<string, unknown>;
    const descriptor: Record<string, string> = {};
    for (const [key, value] of Object.entries(multipart)) {
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        descriptor[key] = String(value);
      } else if (value && typeof value === 'object' && 'name' in value) {
        descriptor[key] = `[file: ${(value as { name: string }).name}]`;
      } else {
        descriptor[key] = '[binary]';
      }
    }
    return JSON.stringify(descriptor);
  }

  return null;
}

/** Mask a cookie value so raw session tokens never reach the cloud. */
function maskCookieValue(value: string): string {
  return value ? '••••' : '';
}

/** Parse a request `Cookie` header (`a=1; b=2`) into masked cookies. */
function parseRequestCookies(headers: Record<string, string> | null | undefined): CapturedCookie[] {
  if (!headers) return [];
  let raw: string | undefined;
  for (const [k, v] of Object.entries(headers)) {
    if (k.toLowerCase() === 'cookie') {
      raw = v;
      break;
    }
  }
  if (!raw) return [];
  const out: CapturedCookie[] = [];
  for (const part of raw.split(';')) {
    const seg = part.trim();
    if (!seg) continue;
    const eq = seg.indexOf('=');
    const name = eq === -1 ? seg : seg.slice(0, eq).trim();
    const value = eq === -1 ? '' : seg.slice(eq + 1).trim();
    if (name) out.push({ name, value: maskCookieValue(value) });
  }
  return out;
}

/** Parse `Set-Cookie` response header values into masked cookies + attributes. */
function parseResponseCookies(setCookieValues: string[]): CapturedCookie[] {
  const out: CapturedCookie[] = [];
  for (const value of setCookieValues) {
    // Playwright's headersArray() usually returns one entry per Set-Cookie, but
    // some runtimes join multiple cookies into one value — split on the
    // `, <token>=` boundary so commas inside Expires=... don't break the parse.
    for (const segRaw of value.split(/,(?=\s*[A-Za-z0-9_.\-]+=)/)) {
      const seg = segRaw.trim();
      if (!seg) continue;
      const parts = seg.split(';');
      const eq = parts[0].indexOf('=');
      if (eq === -1) continue;
      const name = parts[0].slice(0, eq).trim();
      const cookieValue = parts[0].slice(eq + 1).trim();
      const attributes = parts.slice(1).map((p) => p.trim()).filter(Boolean).join(' · ') || undefined;
      if (name) out.push({ name, value: maskCookieValue(cookieValue), attributes });
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// TestInfo-like interface (avoids importing Playwright test internals)
// ---------------------------------------------------------------------------

interface TestInfoLike {
  annotations: Array<{ type: string; description?: string }>;
}

// ---------------------------------------------------------------------------
// ApiRequestTracker
// ---------------------------------------------------------------------------

export class ApiRequestTracker {
  private readonly context: APIRequestContext;
  private readonly originals: Map<string, (...args: never[]) => unknown> = new Map();
  private apiCallWriter: JsonlFileWriter | null = null;
  /**
   * Running tally of response-status ranges.
   *
   * The records go straight to a JSONL file and are never read back in
   * streaming mode, so anything the grid needs about them has to be counted
   * here, while we still hold the record.
   */
  private readonly statusMix = { '2xx': 0, '3xx': 0, '4xx': 0, '5xx': 0, error: 0 };
  private callCounter = 0;
  private apiCallCount = 0;
  private disposed = false;
  private readonly assertionTracker: AssertionTracker | null;
  private readonly apiConfig: ResolvedApiConfig;
  private _lastCallId: string | null = null;

  /** Map of primitive values to their originating call IDs (for status, ok, etc.). */
  private readonly primitiveCallIds = new Map<unknown, string>();

  /** Default API config when none is provided (all capture enabled, default redaction). */
  private static readonly DEFAULT_API_CONFIG: ResolvedApiConfig = Object.freeze({
    trackApiCalls: true,
    captureRequestHeaders: true,
    captureResponseHeaders: true,
    captureRequestBody: true,
    captureResponseBody: true,
    captureAssertions: true,
    redactHeaders: ['authorization', 'cookie', 'set-cookie', 'x-api-key'],
    redactBodyFields: ['password', 'secret', 'token', 'apiKey', 'api_key'],
    apiIncludeUrls: [],
    apiExcludeUrls: [],
  });

  constructor(context: APIRequestContext, assertionTracker?: AssertionTracker, apiConfig?: ResolvedApiConfig) {
    this.context = context;
    this.assertionTracker = assertionTracker ?? null;
    this.apiConfig = apiConfig ?? ApiRequestTracker.DEFAULT_API_CONFIG;
    try {
      this.apiCallWriter = new JsonlFileWriter('api-calls');
    } catch {
      // Graceful degradation
    }
  }

  /** The most recent API call ID (for temporal fallback linking). */
  get lastCallId(): string | null {
    return this._lastCallId;
  }

  /**
   * Get the call ID for a value extracted from a response.
   * Works for objects (via WeakMap) and primitives (via internal Map).
   */
  getCallIdForValue(value: unknown): string | null {
    if (value !== null && value !== undefined && typeof value === 'object') {
      return valueCallIdMap.get(value as object) ?? null;
    }
    return this.primitiveCallIds.get(value) ?? null;
  }

  /** Replace HTTP methods and dispose on the context with instrumented wrappers. */
  intercept(): void {
    for (const method of HTTP_METHODS) {
      const original = (this.context[method] as (...args: never[]) => unknown).bind(this.context);
      this.originals.set(method, original);
      (this.context as unknown as Record<string, unknown>)[method] = this.createWrapper(method, original);
    }

    // Intercept dispose
    const origDispose = this.context.dispose.bind(this.context);
    this.originals.set('dispose', origDispose as (...args: never[]) => unknown);
    (this.context as unknown as Record<string, unknown>).dispose = async (
      options?: { reason?: string },
    ): Promise<void> => {
      this.disposed = true;
      return origDispose(options);
    };
  }

  /** Count one response against the status mix. A null code is a transport error. */
  private tallyStatus(code: number | null): void {
    if (code === null || code <= 0) { this.statusMix.error++; return; }
    if (code >= 500) { this.statusMix['5xx']++; return; }
    if (code >= 400) { this.statusMix['4xx']++; return; }
    if (code >= 300) { this.statusMix['3xx']++; return; }
    if (code >= 200) { this.statusMix['2xx']++; return; }
    this.statusMix.error++;
  }

  /** Return file path, count and status mix for the JSONL file of API call records. */
  getFileData(): {
    apiCallsFile: string | null;
    apiCallsCount: number;
    apiStatusMix: ApiCallsByStatusRange;
  } {
    this.apiCallWriter?.close();
    return {
      apiCallsFile: this.apiCallWriter?.getCount() ? this.apiCallWriter.getPath() : null,
      apiCallsCount: this.apiCallWriter?.getCount() ?? 0,
      apiStatusMix: { ...this.statusMix },
    };
  }

  /** @deprecated Legacy annotations no longer used — data flows via file-based payloads. */
  flushLegacyAnnotations(_testInfo: TestInfoLike): void {
    // No-op in v2
  }

  /** Restore original methods and clear captured data. */
  dispose(): void {
    for (const [method, original] of this.originals) {
      (this.context as unknown as Record<string, unknown>)[method] = original;
    }
    this.originals.clear();
    this.apiCallWriter?.close();
    this.callCounter = 0;
    this.apiCallCount = 0;
    this._lastCallId = null;
    this.primitiveCallIds.clear();
  }

  /** Whether the underlying context has been disposed. */
  get isDisposed(): boolean {
    return this.disposed;
  }

  /** Access captured call count (for testing). */
  getCapturedCallCount(): number {
    return this.apiCallCount;
  }

  /**
   * Wraps response methods to tag extracted values with the originating call ID.
   * This enables assertion linking for values extracted from responses.
   */
  private tagResponseMethods(response: APIResponse, callId: string): void {
    const self = this;

    // Wrap methods that return objects (headers, json)
    const origHeaders = response.headers.bind(response);
    (response as unknown as Record<string, unknown>).headers = function taggedHeaders(): Record<string, string> {
      const result = origHeaders();
      valueCallIdMap.set(result, callId);
      return result;
    };

    const origHeadersArray = response.headersArray.bind(response);
    (response as unknown as Record<string, unknown>).headersArray = function taggedHeadersArray(): Array<{ name: string; value: string }> {
      const result = origHeadersArray();
      valueCallIdMap.set(result, callId);
      return result;
    };

    const origJson = response.json.bind(response);
    (response as unknown as Record<string, unknown>).json = async function taggedJson(): Promise<unknown> {
      const result = await origJson();
      if (result !== null && result !== undefined && typeof result === 'object') {
        valueCallIdMap.set(result as object, callId);
      }
      return result;
    };

    // Wrap methods that return primitives — track in primitiveCallIds
    const origStatus = response.status.bind(response);
    (response as unknown as Record<string, unknown>).status = function taggedStatus(): number {
      const result = origStatus();
      self.primitiveCallIds.set(result, callId);
      return result;
    };

    const origStatusText = response.statusText.bind(response);
    (response as unknown as Record<string, unknown>).statusText = function taggedStatusText(): string {
      const result = origStatusText();
      self.primitiveCallIds.set(result, callId);
      return result;
    };

    const origOk = response.ok.bind(response);
    (response as unknown as Record<string, unknown>).ok = function taggedOk(): boolean {
      const result = origOk();
      self.primitiveCallIds.set(result, callId);
      return result;
    };

    const origText = response.text.bind(response);
    (response as unknown as Record<string, unknown>).text = async function taggedText(): Promise<string> {
      const result = await origText();
      self.primitiveCallIds.set(result, callId);
      return result;
    };

    const origBody = response.body.bind(response);
    (response as unknown as Record<string, unknown>).body = async function taggedBody(): Promise<Buffer> {
      const result = await origBody();
      valueCallIdMap.set(result, callId);
      return result;
    };
  }

  private createWrapper(
    method: HttpMethod,
    original: (...args: never[]) => unknown,
  ): (url: string, options?: Record<string, unknown>) => Promise<APIResponse> {
    const self = this;

    return async function wrappedMethod(
      url: string,
      options?: Record<string, unknown>,
    ): Promise<APIResponse> {
      // URL filtering: skip tracking if URL doesn't match include/exclude patterns
      if (!shouldTrackApiUrl(url, self.apiConfig.apiIncludeUrls, self.apiConfig.apiExcludeUrls)) {
        return (original as (url: string, options?: Record<string, unknown>) => Promise<APIResponse>)(url, options);
      }

      const id = `api-call-${self.callCounter++}`;
      const timestamp = new Date().toISOString();
      const httpMethod = method === 'fetch'
        ? ((options?.method as string) ?? 'GET').toUpperCase()
        : method.toUpperCase();
      const requestBody = extractRequestBody(options);
      const startTime = performance.now();

      // Update temporal tracking for assertion linking
      self._lastCallId = id;
      if (self.assertionTracker) {
        self.assertionTracker.setCurrentCallId(id);
      }

      let response: APIResponse;
      try {
        response = await (original as (url: string, options?: Record<string, unknown>) => Promise<APIResponse>)(
          url,
          options,
        );
      } catch (err: unknown) {
        // Error path: capture partial record, re-throw
        const endTime = performance.now();
        try {
          const errorRequestBody = self.apiConfig.captureRequestBody
            ? redactBodyFields(requestBody, self.apiConfig.redactBodyFields)
            : null;
          const errorRecord: ApiCallRecord = {
            id,
            timestamp,
            method: httpMethod,
            url: stripSecretQueryParams(url),
            requestHeaders: null,
            requestBody: errorRequestBody,
            responseStatusCode: null,
            responseStatusText: null,
            responseHeaders: null,
            responseBody: null,
            responseTimeMs: Math.round((endTime - startTime) * 100) / 100,
            isBinary: false,
            error: err instanceof Error ? err.message : String(err),
          };
          if (self.apiCallWriter) {
            self.apiCallWriter.append(errorRecord);
            self.apiCallCount++;
            self.tallyStatus(null);
          }
        } catch {
          // FR-016: if recording fails, still re-throw the original exception
        }
        throw err;
      }

      // Success path: capture full record with config-driven capture control and redaction
      try {
        const endTime = performance.now();
        const rawResponseHeaders = response.headers();
        const contentType = rawResponseHeaders['content-type'] ?? null;
        const binary = contentType ? !isTextContentType(contentType) : false;

        // Capture control: headers
        // For API calls, request headers come from options.headers (user-provided).
        // Playwright's APIResponse doesn't expose the actual sent request headers,
        // so we capture what we can from the options object.
        let capturedRequestHeaders: Record<string, string> | null = null;
        if (self.apiConfig.captureRequestHeaders && options?.headers) {
          capturedRequestHeaders = options.headers as Record<string, string>;
        }
        let capturedResponseHeaders: Record<string, string> | null = null;
        if (self.apiConfig.captureResponseHeaders) {
          capturedResponseHeaders = rawResponseHeaders;
        }

        // Capture control: bodies
        let capturedRequestBody: string | null = self.apiConfig.captureRequestBody ? requestBody : null;
        let capturedResponseBody: string | null = null;
        if (self.apiConfig.captureResponseBody) {
          try {
            if (binary) {
              const buf = await response.body();
              capturedResponseBody = buf.toString('base64');
            } else {
              capturedResponseBody = await response.text();
            }
          } catch {
            // Body read failed — record what we have
          }
        }

        // Parse cookies BEFORE redaction strips the Cookie/Set-Cookie headers.
        // Values are masked, so the structured fields surface cookie names (and
        // response attributes) for the inspector without leaking session tokens.
        let requestCookies: CapturedCookie[] = [];
        let responseCookies: CapturedCookie[] = [];
        try {
          if (self.apiConfig.captureRequestHeaders) {
            requestCookies = parseRequestCookies(options?.headers as Record<string, string> | undefined);
          }
          if (self.apiConfig.captureResponseHeaders) {
            const setCookieValues = response
              .headersArray()
              .filter((h) => h.name.toLowerCase() === 'set-cookie')
              .map((h) => h.value);
            responseCookies = parseResponseCookies(setCookieValues);
          }
        } catch {
          // Cookie parsing is best-effort — never break capture.
        }

        // Apply redaction to captured data (FR-006: before storage)
        capturedRequestHeaders = redactHeaders(capturedRequestHeaders, self.apiConfig.redactHeaders);
        capturedResponseHeaders = redactHeaders(capturedResponseHeaders, self.apiConfig.redactHeaders);
        capturedRequestBody = redactBodyFields(capturedRequestBody, self.apiConfig.redactBodyFields);
        capturedResponseBody = redactBodyFields(capturedResponseBody, self.apiConfig.redactBodyFields);

        const record: ApiCallRecord = {
          id,
          timestamp,
          method: httpMethod,
          url: stripSecretQueryParams(response.url()),
          requestHeaders: capturedRequestHeaders,
          requestBody: capturedRequestBody,
          responseStatusCode: response.status(),
          responseStatusText: response.statusText(),
          responseHeaders: capturedResponseHeaders,
          responseBody: capturedResponseBody,
          requestCookies: requestCookies.length ? requestCookies : null,
          responseCookies: responseCookies.length ? responseCookies : null,
          responseTimeMs: Math.round((endTime - startTime) * 100) / 100,
          isBinary: binary,
          error: null,
        };
        if (self.apiCallWriter) {
          self.apiCallWriter.append(record);
          self.apiCallCount++;
          self.tallyStatus(record.responseStatusCode);
        }
      } catch {
        // FR-016: never crash the test — silently skip recording
      }

      // Tag the response with the call ID for assertion linking
      try {
        (response as unknown as Record<symbol, string>)[CALL_ID_SYMBOL] = id;
        self.tagResponseMethods(response, id);
      } catch {
        // Tagging failure is non-critical — temporal fallback will be used
      }

      return response;
    };
  }
}
